import type { Client } from "features/container/types";
import { useSession } from "hooks/api/useSession";
import { Button, Popover, Stack, Tooltip } from "prism-react";

import type { AppId } from "utils/clientAppId";

import { AnimatedClientAvatar } from "./AnimatedClientAvatar";
import { MoreClientsList } from "./MoreClientsList";
import { createSortByCurrentAndFollowingClient } from "./utils";

export type ClientsMap = Record<string, Client>;
interface ClientsListProps {
  /**
   * currentClient is undefined on the dashboard
   */
  currentClient?: Client;
  clients: ClientsMap;
  onClientClick?: (client: Client) => void;
  followedClient?: Client;
}

const MAX_VISIBLE_AVATARS = 5;
const AVATARS_VISIBLE_WHEN_OVERFLOW = MAX_VISIBLE_AVATARS - 1;

export const ClientsList: React.FC<ClientsListProps> = ({
  currentClient,
  clients,
  followedClient,
  onClientClick,
}) => {
  const [session] = useSession();

  if (!clients) {
    return null;
  }

  const clientsList = currentClient
    ? Object.values(clients).sort(
        createSortByCurrentAndFollowingClient(currentClient, followedClient),
      )
    : Object.values(clients);
  const clientsCount = clientsList.length;

  const overflowItemsCount =
    clientsCount > MAX_VISIBLE_AVATARS
      ? clientsCount - AVATARS_VISIBLE_WHEN_OVERFLOW
      : 0;
  const visibleClients: Array<Client & { onClick?: () => void }> =
    overflowItemsCount
      ? clientsList.slice(0, AVATARS_VISIBLE_WHEN_OVERFLOW)
      : clientsList;
  const hiddenClients = overflowItemsCount
    ? clientsList.slice(AVATARS_VISIBLE_WHEN_OVERFLOW)
    : [];

  const currentClientUser =
    session.state === "AUTHENTICATED" ? session.user : undefined;

  return (
    /* We use a gap of 0 here to prevent a flash of the tooltip when hovering between the items */
    <Stack css={{ alignItems: "center", height: 24 }} gap={0} horizontal>
      {visibleClients.map((client) => {
        const isCurrentClient = client.clientId === currentClient?.clientId;

        const isDimmed = Boolean(
          followedClient && client.clientId !== followedClient.clientId,
        );

        const src = isCurrentClient
          ? currentClientUser?.avatarUrl
          : client.avatarUrl;
        const name =
          isCurrentClient && currentClientUser
            ? currentClientUser.name
            : client.name;

        return (
          <AnimatedClientAvatar
            key={client.clientId}
            appBackgroundColor="$neutral-bg-base"
            appId={client.appId as AppId}
            aria-label={!isCurrentClient ? `Follow ${client.name}` : undefined}
            color={client.color}
            dimmed={isDimmed}
            isCurrentClient={isCurrentClient}
            layoutId={client.clientId}
            name={name}
            onClick={() => {
              if (client.onClick) {
                client.onClick();
              } else {
                onClientClick?.(client);
              }
            }}
            role="button"
            src={src || undefined}
            variant="row"
          />
        );
      })}

      {hiddenClients.length > 0 && (
        // TODO: When adding Follow mode, we will probably want to convert this to a Dropdown,
        // or make it show in a tooltip (on hover)
        <Popover
          trigger={
            <Tooltip content={`Show ${overflowItemsCount} more`}>
              <Button
                css={{
                  height: "$6",
                  minWidth: "$6",
                  maxWidth: "$6",
                  py: 0,
                  px: 0,
                  marginLeft: "$1",
                  borderRadius: "50%",
                  // Use of !important is a slippery slope.
                  // TODO: Find a better way to override the ghost variant styles
                  backgroundColor: "$neutral-bg-medium !important",
                  fontSize:
                    overflowItemsCount >= 10 && overflowItemsCount < 100
                      ? "9px"
                      : "11px",
                  fontVariantNumeric: "tabular-nums",
                  textAlign: "center",
                }}
              >
                {overflowItemsCount < 100 ? `+${overflowItemsCount}` : "∞"}
              </Button>
            </Tooltip>
          }
        >
          <MoreClientsList
            clients={hiddenClients}
            followedClient={followedClient}
            onClientClick={onClientClick}
          />
        </Popover>
      )}
    </Stack>
  );
};
